/* ============================================================
   cart-drawer.js — THE single reusable Cart component (Ghonché)
   ------------------------------------------------------------
   Ported from the static build's js/cart-drawer.js. The DOM markup,
   rendering functions, and public API shape are unchanged; the state
   layer underneath is swapped from a localStorage-simulated cart to
   the real Shopify Ajax Cart API (/cart.js, /cart/add.js,
   /cart/change.js) — this is the same file the plan's Phase 6 always
   pointed at as the place this swap happens.

   One deliberate content change, not just a mechanical swap: the
   static build's "Free Delivery + 10% OFF over Rs 20,000" second
   milestone tier subtracted a discount client-side that isn't a real
   Shopify discount. Showing "10% OFF" against a total that doesn't
   actually get discounted at checkout would be misleading, so this
   version keeps only the real, single Free Delivery milestone
   (settings.free_ship_threshold) and always displays the real
   cart.total_price — no fake arithmetic. If/when a real Shopify
   automatic discount is configured for large orders, cart.total_price
   will reflect it automatically and this bar's math needs no change.
   ============================================================ */
(function () {
  "use strict";

  var CFG = window.GHONCHE_CONFIG || {};
  var M1 = typeof CFG.freeShippingThreshold === "number" ? CFG.freeShippingThreshold * 100 : 1000000; // cents

  function fmt(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.GHONCHE_MONEY_FORMAT);
    }
    return "Rs. " + Math.round((Number(cents) || 0) / 100).toLocaleString("en-PK");
  }

  function getCdnUrl(url, width) {
    if (!url || typeof url !== "string") return url;
    var parts = url.split("?");
    var q = (parts[1] || "") ? parts[1] + "&width=" + width : "width=" + width;
    return parts[0] + "?" + q;
  }

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function starsHTML(rating) {
    var pct = Math.max(0, Math.min(100, (Number(rating) || 0) / 5 * 100));
    return '<span class="stars" style="--fill:' + pct + '%">★★★★★<span class="stars__fill" aria-hidden="true">★★★★★</span></span>';
  }

  /* ---------- State: the real Shopify cart, refetched after every change ---------- */
  var cart = { items: [], item_count: 0, total_price: 0, original_total_price: 0, items_subtotal_price: 0 };

  function refreshCart() {
    return fetch("/cart.js", { headers: { "Content-Type": "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (data) { cart = data; return cart; });
  }

  function addToCart(variantId, quantity) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw new Error(err.description || "Could not add to cart"); });
        return r.json();
      })
      .then(function (addedItem) {
        return refreshCart().then(function () {
          renderAll();
          toast("Added to bag — " + addedItem.product_title);
          return addedItem;
        });
      })
      .catch(function (err) {
        toast(err.message || "Could not add to cart");
        throw err;
      });
  }

  function setQty(variantId, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { cart = data; renderAll(); return cart; });
  }

  function removeLine(variantId) {
    return setQty(variantId, 0);
  }

  /* ---------- Milestone math (single real tier: free shipping) ---------- */
  function calcTotals() {
    var subtotal = cart.total_price;
    var stage, pct, remaining;
    if (subtotal >= M1) {
      stage = "success"; pct = 100; remaining = 0;
    } else {
      stage = "m1"; remaining = M1 - subtotal;
      pct = Math.min(100, (subtotal / M1) * 100);
    }
    return { subtotal: subtotal, count: cart.item_count, finalTotal: cart.total_price, stage: stage, pct: pct, remaining: remaining };
  }

  /* ---------- Markup (injected once, used by the drawer) ---------- */
  function drawerMarkup() {
    return (
    '<div class="overlay" data-overlay hidden></div>' +
    '<aside class="gcart-drawer" data-cart-drawer role="dialog" aria-modal="true" aria-label="Shopping bag">' +
      '<header class="gcart-drawer__head">' +
        '<h2>Your Bag <span data-cart-count-label></span></h2>' +
        '<button class="icon-btn" data-cart-close aria-label="Close bag"><svg><use href="#i-close"/></svg></button>' +
      "</header>" +
      '<div class="gcart-trustline">' +
        starsHTML(4.8) +
        '<span>Rated 4.8/5.0</span>' +
        '<span class="gcart-trustline__sep" aria-hidden="true">|</span>' +
        '<span class="gcart-trustline__tag">1,600+ Reviews</span>' +
      "</div>" +
      '<div class="gcart-milestone" data-ship-bar>' +
        '<p class="gcart-milestone__text" data-ship-text></p>' +
        '<div class="gcart-milestone__track">' +
          '<div class="gcart-milestone__fill" data-ship-fill></div>' +
        "</div>" +
      "</div>" +
      '<div class="gcart-drawer__body" data-cart-body>' +
        '<div class="gcart-empty" data-cart-empty>' +
          '<svg width="40" height="40" aria-hidden="true"><use href="#i-bag"/></svg>' +
          "<p>Your bag is empty.</p>" +
          '<a class="btn btn--primary" href="/collections/all">Continue Shopping</a>' +
          '<div class="gcart-empty__links">' +
            '<a href="/collections/ready-to-wear">Ready To Wear</a>' +
            '<a href="/collections/unstitched">Unstitched</a>' +
            '<a href="/collections/new-arrivals">New Arrivals</a>' +
          "</div>" +
        "</div>" +
        '<div class="gcart-lines" data-cart-lines></div>' +
      "</div>" +
      '<div class="gcart-drawer__foot" data-cart-foot hidden>' +
        '<div class="gcart-summary">' +
          '<div class="gcart-summary__row gcart-summary__row--total">' +
            '<span>Final Total</span>' +
            '<span class="gcart-summary__total-wrap">' +
              '<span data-cart-final-total>Rs.0</span>' +
            "</span>" +
          "</div>" +
        "</div>" +
        '<a class="btn btn--primary gcart-checkout" href="/checkout" data-cart-checkout>Continue to Secure Checkout</a>' +
        '<div class="gcart-trust-row">' +
          '<span><svg aria-hidden="true"><use href="#i-lock"/></svg>Secure Payment</span>' +
          '<span><svg aria-hidden="true"><use href="#i-refresh"/></svg>Easy Returns</span>' +
          '<span><svg aria-hidden="true"><use href="#i-badgecheck"/></svg>Premium Quality</span>' +
        "</div>" +
        '<a class="gcart-wa" href="https://wa.me/" data-wa-cart target="_blank" rel="noopener"><svg aria-hidden="true"><use href="#i-wa"/></svg>Order on WhatsApp</a>' +
      "</div>" +
    "</aside>"
    );
  }

  var drawer = null, overlay = null;

  function ensureInjected() {
    if (document.querySelector("[data-cart-drawer]")) {
      drawer = document.querySelector("[data-cart-drawer]");
      overlay = document.querySelector("[data-overlay]");
      return;
    }
    document.body.insertAdjacentHTML("beforeend", drawerMarkup());
    drawer = document.querySelector("[data-cart-drawer]");
    overlay = document.querySelector("[data-overlay]");
  }

  function showOverlay(on) {
    if (!overlay) return;
    overlay.hidden = !on;
    if (on) overlay.setAttribute("data-open", ""); else overlay.removeAttribute("data-open");
    if (window.__ghoncheLockPageScroll) window.__ghoncheLockPageScroll(on);
  }

  function isDrawerOpen() { return drawer && drawer.hasAttribute("data-open"); }
  function openDrawer() {
    if (!drawer) return;
    drawer.setAttribute("data-open", "");
    showOverlay(true);
  }
  function closeDrawer() {
    if (drawer) drawer.removeAttribute("data-open");
    if (!(window.__ghoncheNavOpen && window.__ghoncheNavOpen())) showOverlay(false);
  }

  /* ---------- Rendering ---------- */
  function lineHTML(line) {
    var rating = window.GhoncheRatings && line.handle ? window.GhoncheRatings.get(line.handle) : null;
    var size = line.variant_title && line.variant_title !== "Default Title" ? line.variant_title : "";
    return (
      '<article class="gcart-line">' +
        '<button type="button" class="gcart-line__trash" data-remove-variant="' + line.variant_id + '" aria-label="Remove ' + esc(line.product_title) + '"><svg aria-hidden="true"><use href="#i-trash"/></svg></button>' +
        '<a class="gcart-line__thumb" href="' + esc(line.url) + '">' +
          '<img src="' + esc(getCdnUrl(line.image, 160)) + '" alt="' + esc(line.product_title) + '" width="80" height="104" loading="lazy">' +
        "</a>" +
        '<div class="gcart-line__info">' +
          (rating ? '<p class="gcart-line__rating">' + starsHTML(rating.rating) + " " + rating.rating + "/5.0</p>" : "") +
          '<h4 class="gcart-line__title">' + esc(line.product_title) + "</h4>" +
          (size ? '<p class="gcart-line__size">' + esc(size) + "</p>" : "") +
          '<p class="gcart-line__price">' + fmt(line.final_price) + "</p>" +
          '<span class="gcart-line__stock">In stock, ready to deliver</span>' +
          '<div class="gcart-line__row">' +
            '<div class="gcart-qty">' +
              '<button type="button" data-dec-variant="' + line.variant_id + '" data-qty="' + line.quantity + '" aria-label="Decrease quantity"' + (line.quantity <= 1 ? " disabled" : "") + '>&minus;</button>' +
              "<span>" + line.quantity + "</span>" +
              '<button type="button" data-inc-variant="' + line.variant_id + '" data-qty="' + line.quantity + '" aria-label="Increase quantity">+</button>' +
            "</div>" +
            '<span class="gcart-line__total">' + fmt(line.final_line_price) + "</span>" +
          "</div>" +
        "</div>" +
      "</article>"
    );
  }

  function renderAll() {
    var totals = calcTotals();

    document.querySelectorAll("[data-cart-count]").forEach(function (c) {
      c.textContent = totals.count;
      c.hidden = totals.count === 0;
    });

    ensureInjected();
    if (!drawer) return;

    var countLabel = drawer.querySelector("[data-cart-count-label]");
    if (countLabel) countLabel.textContent = totals.count ? "(" + totals.count + ")" : "";

    var linesBox = drawer.querySelector("[data-cart-lines]");
    if (linesBox) linesBox.innerHTML = cart.items.map(lineHTML).join("");

    var empty = cart.items.length === 0;
    var emptyEl = drawer.querySelector("[data-cart-empty]");
    var foot = drawer.querySelector("[data-cart-foot]");
    if (emptyEl) emptyEl.style.display = empty ? "" : "none";
    if (foot) foot.hidden = empty;

    var shipText = drawer.querySelector("[data-ship-text]");
    var shipFill = drawer.querySelector("[data-ship-fill]");
    if (shipText) shipText.textContent = totals.stage === "success" ? "Congrats, you get Free Delivery!" : "Add " + fmt(totals.remaining) + " more for Free Delivery";
    if (shipFill) {
      shipFill.style.width = totals.pct + "%";
      shipFill.classList.toggle("is-success", totals.stage === "success");
    }

    var totalEl = drawer.querySelector("[data-cart-final-total]");
    if (totalEl) totalEl.textContent = fmt(totals.finalTotal);

    var wa = drawer.querySelector("[data-wa-cart]");
    if (wa && CFG.whatsapp) {
      var text = "Assalamualaikum, I want to order:\n" +
        cart.items.map(function (l) { return "• " + l.product_title + (l.variant_title !== "Default Title" ? " (" + l.variant_title + ")" : "") + " x" + l.quantity + " (" + fmt(l.final_line_price) + ")"; }).join("\n") +
        "\nFinal Total: " + fmt(totals.finalTotal);
      wa.href = "https://wa.me/" + CFG.whatsapp + "?text=" + encodeURIComponent(text);
    }

    /* "Paired Well With" removed from the cart drawer per request (kept
       on the product page's own Paired Well With section) - always
       stays hidden here now instead of being populated. */
    var pairedSec = drawer.querySelector("[data-cart-paired]");
    if (pairedSec) { pairedSec.hidden = true; }
  }

  function toast(msg) {
    var toasts = document.querySelector("[data-toasts]");
    if (!toasts) return;
    var el = document.createElement("div");
    el.className = "toast";
    el.textContent = msg;
    toasts.appendChild(el);
    setTimeout(function () { el.remove(); }, 3000);
  }

  /* ---------- Wire-up ---------- */
  ensureInjected();
  refreshCart().then(renderAll);

  document.addEventListener("click", function (e) {
    var toggle = e.target.closest("[data-cart-toggle]");
    if (toggle) { openDrawer(); return; }
    var close = e.target.closest("[data-cart-close]");
    if (close) { closeDrawer(); return; }
    if (overlay && e.target === overlay) { closeDrawer(); }

    var quick = e.target.closest("[data-quick-add]");
    if (quick) {
      addToCart(quick.dataset.variantId, 1).then(function () { openDrawer(); });
      return;
    }
    var recAdd = e.target.closest("[data-rec-add]");
    if (recAdd) { addToCart(recAdd.dataset.variantId, 1); return; }

    var inc = e.target.closest("[data-inc-variant]");
    if (inc) { setQty(inc.dataset.incVariant, Number(inc.dataset.qty) + 1); return; }
    var dec = e.target.closest("[data-dec-variant]");
    if (dec) {
      var q = Number(dec.dataset.qty);
      if (q > 1) setQty(dec.dataset.decVariant, q - 1);
      return;
    }
    var rem = e.target.closest("[data-remove-variant]");
    if (rem) { removeLine(rem.dataset.removeVariant); return; }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") closeDrawer();
  });

  /* ---------- Public API ---------- */
  window.GhoncheCart = {
    getCart: function () { return cart; },
    addToCart: addToCart, setQty: setQty, removeLine: removeLine, refreshCart: refreshCart,
    openDrawer: openDrawer, closeDrawer: closeDrawer, isDrawerOpen: isDrawerOpen,
    calcTotals: calcTotals,
    lineHTML: lineHTML, fmt: fmt, getCdnUrl: getCdnUrl, esc: esc,
    renderAll: renderAll, toast: toast, M1: M1
  };

  window.getCart = window.GhoncheCart.getCart;
  window.renderCart = renderAll;
  window.openCart = openDrawer;
  window.closeCart = closeDrawer;
})();
